import React, { useEffect, useState } from 'react';
import Modal from '../common/models/Modal';
import FormField from '../UI/FormField';
import { Info, Plus } from 'lucide-react';
import { Method, showToastNotification } from '@/utility/helper';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import { callApi } from '@/api/callApi';

interface AddVariantProp {
  modelType?: 'add' | 'update';
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subTitle?: string;
  variantId?: string;
  variantName?: string;
}

interface StatesProp {
  variant: string;
  isSubmitting: boolean;
  error: string | null;
}

const AddVariant: React.FC<AddVariantProp> = ({
  show,
  onClose,
  onSuccess,
  modelType = 'add',
  subTitle,
  variantName,
  variantId,
}) => {
  const [states, setStates] = useState<StatesProp>({
    variant: '',
    isSubmitting: false,
    error: null,
  });

  useEffect(() => {
    if (variantName) handleChangeState({ variant: variantName });
  }, [variantName]);

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const onSubmit = async () => {
    try {
      if (states.variant.length === 0) {
        handleChangeState({ error: 'Variant name is required' });
        return;
      }
      handleChangeState({ isSubmitting: true });
      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.variants}`;
      let method: Method;
      if (variantId) {
        PATH = `${PATH}/${variantId}`;
        method = 'PATCH';
      } else {
        PATH = PATH;
        method = 'POST';
      }
      await callApi(PATH, { method: method, data: { name: states.variant } });
      onSuccess();
      onClose();
      showToastNotification(
        'success',
        variantId
          ? 'Variant updated sucessfully'
          : 'Variant created successfully',
      );
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
    } finally {
      handleChangeState({ isSubmitting: false });
    }
  };

  const handleVariantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const variant = e.target.value;
    handleChangeState({ variant: variant });
    if (!variant || variant.length < 3) {
      handleChangeState({ error: 'Please enter variant name atleast 3 char.' });
    } else {
      handleChangeState({ error: '' });
    }
  };

  return (
    <>
      <Modal
        show={show}
        onClose={onClose}
        title={modelType === 'add' ? 'Add Variant' : 'Update Variant'}
      >
        <div className="p-4 h-full flex flex-col justify-between space-y-4">
          {subTitle && <p className="text-gray-600 text-sm mb-4">{subTitle}</p>}

          <div>
            <FormField
              value={states.variant}
              onChange={(e) =>
                handleVariantChange(e as React.ChangeEvent<HTMLInputElement>)
              }
              placeholder="Enter variant name"
            />
            {states.error && (
              <span
                className={`text-red-500 text-sm flex items-start gap-1 mt-1 `}
              >
                <Info size={16} /> {states.error}
              </span>
            )}
          </div>
          <div className="flex justify-end space-x-4">
            <button
              className="btn-secondary"
              onClick={onClose}
              disabled={states.isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSubmit()}
              className="btn-primary"
              disabled={states.isSubmitting}
            >
              {states.isSubmitting ? (
                <span>Submitting...</span>
              ) : (
                <>
                  <Plus size={16} /> {modelType === 'add' ? 'Add' : 'Update'}{' '}
                  Variant
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddVariant;
